import type { PrivateFile } from './document';
import type { TrainingStatus } from './training';

// needed for zod
export const HABILITATION_ISSUER_KIND_VALUES = [
  'EMPLOYER',
  'THIRD_PARTY',
  'EITHER',
] as const;

export enum HabilitationIssuerKindEnum {
  EMPLOYER = 'EMPLOYER',
  THIRD_PARTY = 'THIRD_PARTY',
  EITHER = 'EITHER',
}

export type HabilitationIssuerKind =
  (typeof HABILITATION_ISSUER_KIND_VALUES)[number];

export const HABILITATION_STATUS_VALUES = [
  'VALID',
  'EXPIRE_SOON',
  'EXPIRED',
] as const;

export enum HabilitationStatusEnum {
  VALID = 'VALID',
  EXPIRE_SOON = 'EXPIRE_SOON',
  EXPIRED = 'EXPIRED',
}

export type HabilitationStatus = (typeof HABILITATION_STATUS_VALUES)[number];

// Catalogue par organisation, entièrement défini par l'admin — pas de preset
// par pays (contrairement à MedicalVisitType).
export type HabilitationType = {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  label: string;
  description: string | null;
  periodicityYears: number | null;
  issuerKind: HabilitationIssuerKind;
  // Décoché par défaut : rend le champ formation obligatoire au moment de
  // créer/renouveler une Habilitation de ce type — contrainte de formulaire
  // uniquement, jamais recalculée rétroactivement.
  requiresTraining: boolean;
  organizationId: number;
  // Nombre d'employés distincts ayant au moins un enregistrement Habilitation
  // de ce type — présent uniquement sur la liste (findAllForOrg), pas sur les
  // réponses de create/update/findOne qui ne recalculent pas l'agrégat.
  usersCount?: number;
};

export type HabilitationUser = {
  id: number;
  firstName: string;
  lastName: string;
  email: string | null;
};

export type HabilitationTraining = {
  id: number;
  name: string;
  status: TrainingStatus;
};

// Pas d'affectation préalable (pas d'équivalent UserMedicalVisitType) : qui a
// besoin de quelle habilitation change trop souvent pour être prédéclaré —
// voir HabilitationType. Un salarié sans aucune Habilitation reste NA.
export type Habilitation = {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  date: Date;
  nextDueDate: Date | null;
  status: HabilitationStatus;
  // relations
  organizationId: number;
  typeId: number;
  type: HabilitationType;
  userId: number;
  user: HabilitationUser;
  createdById: number;
  // Formation justificative optionnelle — si elle expire, cette habilitation
  // est considérée non conforme même si nextDueDate n'est pas atteinte.
  trainingId: number | null;
  training: HabilitationTraining | null;
  files: PrivateFile[];
};
